import { InternalLinkSchema } from '@buyerflo/core/schemas/link'
import { InternalNotificationSchema } from '@buyerflo/core/schemas/notification'
import { InternalPurchaserSchema, PurchaserListFilters } from '@buyerflo/core/schemas/purchaser'
import { ShowInTabOption } from '@buyerflo/core/schemas/purchaserConfig'
import { SharedTodoSchema } from '@buyerflo/core/schemas/todo'
import { filterAndSortPurchasers } from '@buyerflo/ui/utils/purchaser'
import {
    useUsersQuery,
    useFindUserFromId,
    useUserName,
    useGroupsQuery,
    useConjunctionalAgenciesQuery,
    useSavedFiltersQuery,
    useAutomationsQuery,
    useAutomationSettingsQuery,
    useChatGroupsQuery,
    useChatConfigQuery,
    useChatApi,
    useTodoQuery,
    useSharedTodosQuery,
    useOrgDetailsQuery,
    useOrgIntegrationsQuery,
    useRolesQuery,
    useNotificationsQuery,
    useXeroAccountsQuery,
    useXeroItemsQuery,
} from '@buyerflo/ui/hooks/api'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useWebSocket } from 'apiProviders/websocketProvider'
import { useUserData } from 'auth'
import { useOrgId } from 'helpers'
import { usePurchaserContext } from 'purchasers/purchaserProvider'
import { groupBy, map, pipe, prop, sortBy } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTRPC, useTRPCClient } from 'utils/trpc'

// Re-export shared hooks
export {
    useUsersQuery,
    useFindUserFromId,
    useUserName,
    useGroupsQuery,
    useConjunctionalAgenciesQuery,
    useSavedFiltersQuery,
    useAutomationsQuery,
    useAutomationSettingsQuery,
    useChatGroupsQuery,
    useChatConfigQuery,
    useChatApi,
    useTodoQuery,
    useSharedTodosQuery,
    useOrgDetailsQuery,
    useOrgIntegrationsQuery,
    useRolesQuery,
    useNotificationsQuery,
    useXeroAccountsQuery,
    useXeroItemsQuery,
}

// Purchasers — the whole org's client list is fetched in one tRPC query and then
// searched, filtered and sorted in the browser. `params` is local filter state,
// so changing a filter re-derives the list without another round trip.
export const usePurchasersApi = (sub = false) => {
    const trpc = useTRPC()
    const usersQuery = useUsersQuery()

    const [params, setParams] = useState<PurchaserListFilters>({
        status: [],
        agentIds: [],
        search: '',
        actionFilters: {},
        sortDirection: 'desc',
    })

    const query = useQuery(trpc.purchaser.getPurchasers.queryOptions())

    const all = query.data as InternalPurchaserSchema[] | undefined

    const data = useMemo(() => {
        if (!all) return undefined

        const users = usersQuery.data ?? []
        const lookup = (userId: string) => {
            const user = users.find(u => u.userId == userId)
            return user ? `${user.firstName} ${user.lastName}` : ''
        }

        return filterAndSortPurchasers(all, params, lookup)
    }, [all, usersQuery.data, params])

    return {
        ...query,
        data,
        /** Number of clients after filtering — what the list header counts. */
        found: data?.length,
        isLoading: query.isLoading,
        error: query.error,
        refresh: query.refetch,
        params,
        setParams,
    }
}

// Users
export const useUsersApi = (filterActive = false) => {
    const query = useUsersQuery()
    const { data } = query

    const filteredData = useMemo(() => {
        if (!data) return undefined
        const filtered = data.filter(user => (filterActive ? user.active : true))
        return sortBy(x => x.firstName, filtered)
    }, [data, filterActive])

    return {
        ...query,
        data: filteredData,
        refresh: query.refetch,
    }
}

// Groups
export const useGroupsApi = () => {
    const query = useGroupsQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Links
export const useLinksQuery = () => {
    const trpc = useTRPC()
    const linksQuery = useQuery(trpc.links.getLinks.queryOptions())
    const categoryOrderQuery = useQuery(trpc.links.getLinkCategoryOrder.queryOptions())

    return useQuery({
        queryKey: ['links', 'grouped', linksQuery.dataUpdatedAt, categoryOrderQuery.dataUpdatedAt],
        queryFn: () => {
            const links = (linksQuery.data as unknown as InternalLinkSchema[]) || []
            const categoryOrderData = (categoryOrderQuery.data as unknown as {
                linkCategoryOrder: string[]
            }) || { linkCategoryOrder: [] }
            const categoryOrder: string[] = categoryOrderData.linkCategoryOrder || []

            return sortBy(
                ([category, _]: [string, InternalLinkSchema[]]) => {
                    const order = categoryOrder.indexOf(category)
                    return order == -1 ? 999 : order
                },
                Object.entries(groupBy((link: InternalLinkSchema) => link.category || '', links)),
            ).map(([category, links]: [string, InternalLinkSchema[]]) => ({
                category,
                links: sortBy((link: InternalLinkSchema) => link.order || 0, links),
            }))
        },
        enabled: linksQuery.isSuccess && categoryOrderQuery.isSuccess,
    })
}

export const useLinksApi = () => {
    const query = useLinksQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Conjunctional Agencies
export const useConjunctionalAgenciesApi = () => {
    const query = useConjunctionalAgenciesQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Purchaser Config
export const usePurchaserConfigQuery = () => {
    const trpc = useTRPC()
    return useQuery({
        ...trpc.purchaserConfig.getPurchaserValueConfig.queryOptions(),
        select: data => sortBy(prop('configOrder'), data),
    })
}

export const useAllPurchaserConfigApi = () => {
    const query = usePurchaserConfigQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

export const usePurchaserConfigApi = (filter?: ShowInTabOption) => {
    const { user } = useUserData()
    const query = usePurchaserConfigQuery()

    const filteredData = useMemo(() => {
        if (!query.data) return undefined
        return (filter ? query.data.filter(x => x.showInTab == filter) : query.data).filter(
            x => user.superUser || (x.visibleTo?.includes(user.roleId) ?? true),
        )
    }, [query.data, filter, user.roleId, user.superUser])

    return {
        ...query,
        data: filteredData,
        refresh: query.refetch,
    }
}

// Saved Filters
export const useSavedFiltersApi = () => {
    const query = useSavedFiltersQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Automations
export const useAutomationsApi = () => {
    const query = useAutomationsQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

export const useAutomationSettingsApi = () => {
    const query = useAutomationSettingsQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Notifications
export const useNotificationsApi = () => {
    const trpc = useTRPC()
    const trpcClient = useTRPCClient()
    const wsNotification = useWebSocket('notification') as any
    const wsNotificationsSeen = useWebSocket('notificationsSeen') as any
    const queryClient = useQueryClient()

    const query = useInfiniteQuery({
        queryKey: ['notifications', 'infinite'],
        queryFn: async ({ pageParam }: { pageParam?: { ExclusiveStartKey?: any } }) => {
            const result = await (trpcClient as any).notifications.getNotifications.query({
                Limit: 20,
                ExclusiveStartKey: pageParam?.ExclusiveStartKey,
            })
            return result
        },
        initialPageParam: undefined as { ExclusiveStartKey?: any } | undefined,
        getNextPageParam: (lastPage: any) => {
            const lastEvaluatedKey = Array.isArray(lastPage) ? undefined : (lastPage as any)?.LastEvaluatedKey
            return lastEvaluatedKey ? { ExclusiveStartKey: lastEvaluatedKey } : undefined
        },
    })

    // Derive notifications from query cache (single source of truth)
    const notifications = useMemo(() => {
        if (!query.data?.pages) return []
        return query.data.pages.flatMap(page => {
            const pageData = Array.isArray(page) ? page : (page as any).Items || []
            return pageData as InternalNotificationSchema[]
        })
    }, [query.data])

    // WebSocket: new notification arrived - refetch
    useEffect(() => {
        if (wsNotification) {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        }
    }, [wsNotification, queryClient])

    // WebSocket: notifications marked seen on another device - refetch
    useEffect(() => {
        if (wsNotificationsSeen) {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        }
    }, [wsNotificationsSeen, queryClient])

    const setAllSeenMutation = useMutation(trpc.notifications.setAllSeen.mutationOptions())
    const setNotificationMutation = useMutation(trpc.notifications.setNotification.mutationOptions())
    const setAllReadMutation = useMutation(trpc.notifications.setAllRead.mutationOptions())

    const setAllSeen = useCallback(async () => {
        const unseen = notifications.filter(n => !n.seen)
        if (unseen.length === 0) return

        queryClient.setQueryData(
            ['notifications', 'infinite'],
            (old: any) => {
                if (!old?.pages) return old
                return {
                    ...old,
                    pages: old.pages.map((page: any) => {
                        if (Array.isArray(page)) return page
                        return {
                            ...page,
                            Items: (page.Items || []).map((n: any) => ({ ...n, seen: true })),
                        }
                    }),
                }
            },
        )

        await setAllSeenMutation.mutateAsync()
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }, [notifications, setAllSeenMutation, queryClient])

    const markAsRead = useCallback(async (notificationId: string) => {
        queryClient.setQueryData(
            ['notifications', 'infinite'],
            (old: any) => {
                if (!old?.pages) return old
                return {
                    ...old,
                    pages: old.pages.map((page: any) => {
                        if (Array.isArray(page)) return page
                        return {
                            ...page,
                            Items: (page.Items || []).map((n: any) =>
                                n.notificationId === notificationId ? { ...n, read: true } : n,
                            ),
                        }
                    }),
                }
            },
        )

        await setNotificationMutation.mutateAsync({ notificationId, read: true })
    }, [setNotificationMutation, queryClient])

    const markAllRead = useCallback(async () => {
        const unread = notifications.filter(n => !n.read)
        if (unread.length === 0) return

        queryClient.setQueryData(
            ['notifications', 'infinite'],
            (old: any) => {
                if (!old?.pages) return old
                return {
                    ...old,
                    pages: old.pages.map((page: any) => {
                        if (Array.isArray(page)) return page
                        return {
                            ...page,
                            Items: (page.Items || []).map((n: any) => ({ ...n, read: true })),
                        }
                    }),
                }
            },
        )

        await setAllReadMutation.mutateAsync()
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }, [notifications, setAllReadMutation, queryClient])

    return {
        ...query,
        data: notifications,
        setAllSeen,
        markAsRead,
        markAllRead,
        lastPage: !query.hasNextPage,
        nextPage: query.fetchNextPage,
    }
}

// Chat Groups
export const useChatGroupsApi = () => {
    const query = useChatGroupsQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Chat Config
export const useChatConfigApi = () => {
    const query = useChatConfigQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Todos
export const useTodoApi = () => {
    const query = useTodoQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Org Details
export const useOrgDetailsApi = () => {
    const query = useOrgDetailsQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Shared Todos
export const useSharedTodosApi = (showAll = false) => {
    const { user } = useUserData()
    const query = useSharedTodosQuery()

    const filteredData = useMemo(() => {
        if (!query.data) return undefined
        const data = query.data as unknown as SharedTodoSchema[]
        return showAll ? data : data.filter((x: SharedTodoSchema) => x.userIds?.includes(user.userId))
    }, [query.data, showAll, user.userId])

    return {
        ...query,
        data: filteredData,
        refresh: query.refetch,
    }
}

// Purchaser Files - Still uses REST API
export interface PurchaserFilesApiData {
    isFolder: boolean
    isEmail: boolean
    Key: string
    name: string
    Size: number
    /** ISO timestamp string from S3 (serialised over tRPC without a transformer). */
    LastModified?: string
    loading?: boolean
}

export const usePurchaserFilesQuery = (purchaserId: string) => {
    const trpc = useTRPC()
    const orgId = useOrgId()
    const locationRoot = `${orgId}/${purchaserId}/files/`

    return useQuery({
        ...trpc.purchaser.getFiles.queryOptions({ purchaserId }),
        select: data =>
            pipe(
                map(({ Key, Size, LastModified, loading }) => {
                    const isFolder = Key.endsWith('/')
                    const newKey = Key.replace(locationRoot, '')

                    return {
                        isFolder,
                        isEmail: Key.endsWith('.eml') || Key.endsWith('.msg'),
                        Key: newKey,
                        name: newKey.replace(/\/$/, ''),
                        Size,
                        LastModified,
                        loading,
                    }
                }) as any,
                sortBy((x: any) => x.name),
                sortBy((x: any) => (x.isFolder ? 0 : 1)),
            )(data),
    })
}

export const usePurchaserFilesApi = (
    { excludeFolders }: { excludeFolders?: boolean } = { excludeFolders: false },
) => {
    const { purchaser } = usePurchaserContext()
    const query = usePurchaserFilesQuery(purchaser.purchaserId)

    const filteredData = useMemo(() => {
        if (!query.data) return undefined
        return excludeFolders ? query.data.filter(x => !x.isFolder) : query.data
    }, [query.data, excludeFolders])

    return {
        ...query,
        data: filteredData,
        refresh: query.refetch,
    }
}

// Calendar Events
export const useCalendarEventsApi = () => {
    const trpc = useTRPC()

    const [params, setParams] = useState<{
        start?: number
        end?: number
        agentId?: string
    }>({})

    const query = useQuery({
        ...(params.start !== undefined && params.end !== undefined
            ? trpc.calendar.searchEvents.queryOptions(
                  params as { start: number; end: number; agentId?: string },
              )
            : trpc.calendar.searchEvents.queryOptions({ start: 0, end: 0 })),
        enabled: params.start !== undefined && params.end !== undefined,
    })

    return {
        ...query,
        data: query.data ?? [],
        refresh: query.refetch,
        params,
        setParams,
    }
}

// Org Integrations
export const useOrgIntegrationsApi = () => {
    const query = useOrgIntegrationsQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Roles
export const useRolesApi = () => {
    const query = useRolesQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Xero Accounts
export const useXeroAccountsApi = () => {
    const query = useXeroAccountsQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}

// Xero Items
export const useXeroItemsApi = () => {
    const query = useXeroItemsQuery()
    return {
        ...query,
        refresh: query.refetch,
    }
}
